import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';

const Background = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, transparent 0%, rgba(0,0,0,0.6) 100%);
  }
`;

const Canvas = styled.canvas`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const StaticNoiseBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const { innerWidth, innerHeight, devicePixelRatio } = window;
      const ratio = Math.max(1, Math.min(devicePixelRatio || 1, 2));
      canvas.width = Math.floor(innerWidth * ratio);
      canvas.height = Math.floor(innerHeight * ratio);
      canvas.style.width = `${innerWidth}px`;
      canvas.style.height = `${innerHeight}px`;
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    };

    resize();
    window.addEventListener('resize', resize);

    // Throttle updates to reduce flicker
    const targetFps = 1; // adjust to taste
    const frameInterval = 1000 / targetFps;

    const render = () => {
      const now = performance.now();
      if (now - lastTimeRef.current < frameInterval) {
        animationRef.current = requestAnimationFrame(render);
        return;
      }
      lastTimeRef.current = now;

      const width = canvas.width;
      const height = canvas.height;
      const imageData = ctx.createImageData(width, height);
      const data = imageData.data;

      // Fill with grayscale noise
      for (let i = 0; i < data.length; i += 4) {
        const v = Math.random() * 255; // 0..255
        const gray = v; // grayscale
        data[i] = gray;
        data[i + 1] = gray;
        data[i + 2] = gray;
        data[i + 3] = 255; // alpha
      }

      ctx.putImageData(imageData, 0, 0);
      animationRef.current = requestAnimationFrame(render);
    };

    animationRef.current = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  return (
    <Background>
      <Canvas ref={canvasRef} />
    </Background>
  );
};

export default StaticNoiseBackground;


